/**
 * API Client Library for ERP System
 * Handles authentication and API calls to the backend
 */

// Dynamically resolve API URL based on current browser host for dev/LAN support
const _envApiUrl = import.meta.env.VITE_ERP_API_URL || import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL;
const API_BASE_URL = (_envApiUrl && _envApiUrl.trim())
    ? _envApiUrl.trim()
    : `http://${window.location.hostname}:8000/api`;


class ApiClient {
    constructor() {
        this.baseURL = API_BASE_URL;
        this.token = localStorage.getItem('erp_auth_token');
    }

    setToken(token) {
        this.token = token;
        if (token) {
            localStorage.setItem('erp_auth_token', token);
        } else {
            localStorage.removeItem('erp_auth_token');
        }
    }

    getToken() {
        return this.token || localStorage.getItem('erp_auth_token');
    }

    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const token = this.getToken();

        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
                ...options.headers,
            },
            ...options,
        };

        if (options.body && typeof options.body === 'object') {
            config.body = JSON.stringify(options.body);
        }

        try {
            const response = await fetch(url, config);

            // Handle 401 Unauthorized - clear token and redirect to login
            // Only redirect if user is on an ERP route, not on public website routes
            const hasLocalAuth = !!localStorage.getItem('laborpro_local_auth');
            if (response.status === 401 && endpoint !== '/auth/login' && endpoint !== '/auth/register' && !hasLocalAuth) {
                this.setToken(null);
                const currentPath = window.location.pathname;
                // Only redirect to ERP login if user is on an ERP route
                if (currentPath.startsWith('/erp/') && currentPath !== '/erp/login') {
                    window.location.href = '/erp/login';
                }
                throw new Error('Session expired. Please login again.');
            }

            let data;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                try {
                    // Read response as text first (can be cloned), then parse as JSON
                    const responseText = await response.text();
                    try {
                        data = JSON.parse(responseText);
                    } catch (parseError) {
                        data = {
                            success: false,
                            error: 'Invalid JSON response from server',
                            message: responseText.substring(0, 200)
                        };
                    }
                } catch (textError) {
                    data = {
                        success: false,
                        error: 'Failed to read response body',
                        message: textError.message || 'Unknown error'
                    };
                }
            } else {
                const text = await response.text();
                try {
                    data = JSON.parse(text);
                } catch {
                    data = {
                        success: false,
                        error: 'Non-JSON response from server',
                        message: text.substring(0, 200)
                    };
                }
            }

            if (!response.ok) {
                const errorMsg = data.error || data.message || 'Request failed';
                const safeErrorMsg = typeof errorMsg === 'string' ? errorMsg : String(errorMsg);
                throw new Error(safeErrorMsg);
            }

            return data;
        } catch (error) {
            if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
                throw new Error('Cannot connect to API server. Please check if the backend is running.');
            }
            if (error && error.message) {
                throw new Error(String(error.message));
            }
            throw new Error('An unexpected error occurred');
        }
    }

    // Auth methods
    async signIn(email, password) {
        const data = await this.request('/auth/login', {
            method: 'POST',
            body: { email, password },
        });

        if (data.success && data.data?.session?.access_token) {
            this.setToken(data.data.session.access_token);
        }

        const userProfile = data.data?.userProfile || data.data?.user;
        let dashboardRoute = '/erp/dashboard-overview';

        if (userProfile?.role) {
            switch (userProfile.role) {
                case 'super_admin':
                    dashboardRoute = '/erp/dashboard-overview';
                    break;
                case 'hr_admin':
                    dashboardRoute = '/erp/employee-management';
                    break;
                case 'telecaller':
                    dashboardRoute = '/erp/telecaller-dashboard';
                    break;
                case 'staff':
                    dashboardRoute = '/erp/client-requirements-matching';
                    break;
                case 'client':
                    dashboardRoute = '/erp/invoice-billing-management';
                    break;
                case 'employee':
                    dashboardRoute = '/erp/employee-self-service';
                    break;
                default:
                    dashboardRoute = '/erp/dashboard-overview';
            }
        }

        return {
            success: data.success,
            data: {
                session: {
                    user: data.data?.user,
                    access_token: data.data?.session?.access_token,
                },
            },
            userProfile: userProfile,
            dashboardRoute: dashboardRoute,
            error: data.error,
        };
    }

    async getSession() {
        try {
            const data = await this.request('/auth/session');
            return {
                success: data.success,
                data: {
                    session: {
                        user: data.data?.session?.user,
                    },
                },
            };
        } catch (error) {
            if (error.message.includes('Session expired') || error.message.includes('Authentication required')) {
                this.setToken(null);
            }
            return {
                success: false,
                data: { session: null },
                error: error.message,
            };
        }
    }

    async getUserProfile(userId) {
        try {
            const data = await this.request(`/auth/profile${userId ? `/${userId}` : ''}`);
            return {
                success: data.success,
                data: data.data,
            };
        } catch (error) {
            return {
                success: false,
                error: error.message,
            };
        }
    }

    async signOut() {
        try {
            await this.request('/auth/logout', { method: 'POST' });
            this.setToken(null);
            return { success: true };
        } catch (error) {
            this.setToken(null);
            return { success: true };
        }
    }

    async resetPassword(email) {
        const data = await this.request('/auth/resetPassword', {
            method: 'POST',
            body: { email },
        });
        return {
            success: data.success,
            error: data.error,
        };
    }

    // Generic CRUD methods
    async get(endpoint, params = {}) {
        try {
            const queryString = new URLSearchParams(params).toString();
            const url = queryString ? `${endpoint}?${queryString}` : endpoint;
            const data = await this.request(url, { method: 'GET' });
            return {
                success: data.success,
                data: data.data,
                error: data.error,
            };
        } catch (error) {
            let errorMessage = 'Request failed';
            if (error && typeof error === 'object') {
                errorMessage = error.message || error.error || String(error);
            } else if (error) {
                errorMessage = String(error);
            }
            return {
                success: false,
                data: null,
                error: errorMessage,
            };
        }
    }

    async post(endpoint, body) {
        const data = await this.request(endpoint, {
            method: 'POST',
            body,
        });
        return {
            success: data.success,
            data: data.data,
            error: data.error,
        };
    }

    async put(endpoint, body) {
        const data = await this.request(endpoint, {
            method: 'PUT',
            body,
        });
        return {
            success: data.success,
            data: data.data,
            error: data.error,
        };
    }

    async delete(endpoint) {
        const data = await this.request(endpoint, {
            method: 'DELETE',
        });
        return {
            success: data.success,
            error: data.error,
        };
    }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;

